import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from 'utils/services';

import { InsuranceProvider, SelectInsuranceProvider } from 'models/insurance.types';

import {
  AddInsuranceReqProps,
  AddInsuranceResProps,
  BenefitInsuranceStatusResProps,
  DeleteInsuranceReqProps,
  DeleteInsuranceResProps,
  DeletePrimaryPreviewReqProps,
  DeletePrimaryPreviewResProps,
  DeletePrimaryReqProps,
  DeletePrimaryResProps,
  DeleteSecondaryResProps,
  GetEligibilityCheckResProps,
  GetInsurancesResProps,
  GetWizardInsurancePlansResProps,
  MedicareConsentResProps,
  PatchPrimaryInsuranceReqProps,
  PatchPrimaryInsuranceResProps,
  PostEligibilityCheckReqProps,
  PostEligibilityCheckResProps,
  PrimaryInsuranceCommitReqProps,
  PrimaryInsuranceCommitResProps,
  PrimaryInsurancePreviewReqProps,
  PrimaryInsurancePreviewResProps,
  UpdateInsuranceReqProps,
  ValidateMedicareReqProps,
  ValidateMedicareResProps,
  WizardInsurancePlan
} from './insurance.types';
import { normalizeWizardPlansResponse } from './insuranceWizard.mappers';

export const insuranceApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (build) => ({
    addInsurance: build.mutation<AddInsuranceResProps, AddInsuranceReqProps>({
      invalidatesTags: ['Insurance'],
      query: ({ accessToken, ...body }) => ({
        body,
        ...(accessToken && {
          headers: {
            Authorization: accessToken
          }
        }),
        method: 'POST',
        url: '/insurances'
      })
    }),
    deleteInsurance: build.mutation<DeleteInsuranceResProps, DeleteInsuranceReqProps>({
      invalidatesTags: ['Insurance'],
      query: ({ id }) => ({
        method: 'DELETE',
        url: `/insurances/${id}`
      })
    }),
    deletePrimaryInsurance: build.mutation<DeletePrimaryResProps, DeletePrimaryReqProps>({
      invalidatesTags: ['Insurance'],
      query: (body) => ({
        body,
        method: 'DELETE',
        url: '/patient-portal/insurance/primary'
      })
    }),
    deleteSecondaryInsurance: build.mutation<DeleteSecondaryResProps, void>({
      invalidatesTags: ['Insurance'],
      query: () => ({
        method: 'DELETE',
        url: '/patient-portal/insurance/secondary'
      })
    }),
    getBenefitInsuranceStatus: build.query<BenefitInsuranceStatusResProps, void>({
      providesTags: ['Insurance'],
      query: () => ({
        url: '/insurances/benefits-check'
      })
    }),
    /** Poll a wizard eligibility check (async edit-flow verification). Behind `patientPortalInsuranceWizard`. */
    getEligibilityCheck: build.query<GetEligibilityCheckResProps, { checkId: string }>({
      query: ({ checkId }) => ({
        url: `/patient-portal/insurance/eligibility/checks/${checkId}`
      })
    }),
    getInsuranceProviders: build.query<
      SelectInsuranceProvider[],
      { accessToken?: string; state: string }
    >({
      query: ({ accessToken, ...params }) => ({
        headers: {
          ...(accessToken && { Authorization: accessToken })
        },
        params,
        url: '/insurances/plans'
      }),
      transformResponse: (response: { data: InsuranceProvider[] }) => {
        return response.data
          .map((provider) => ({
            _id: provider._id,
            insuranceType: provider.insuranceType,
            isDiscoverable: provider.isDiscoverable,
            label: provider.name,
            value: provider.payerId
          }))
          .toSorted((a, b) => a.label.localeCompare(b.label));
      }
    }),
    getInsurances: build.query<GetInsurancesResProps, void>({
      providesTags: ['Insurance'],
      query: () => ({
        url: '/insurances'
      })
    }),
    getMedicareConsentRequired: build.query<MedicareConsentResProps, { state: string }>({
      query: ({ state }) => ({
        params: { state },
        url: '/patient-portal/insurance/medicare-consent-required'
      })
    }),
    /** Plans probe for wizard availability (`useInsuranceWizardAvailability`) — Preston §5 on develop. */
    /** BE schema allows `state` only; filter plans client-side until Preston adds `search`. */
    getWizardInsurancePlans: build.query<WizardInsurancePlan[], { state: string }>({
      query: ({ state }) => ({
        params: { state },
        url: '/patient-portal/insurance/plans'
      }),
      transformResponse: (response: GetWizardInsurancePlansResProps) =>
        normalizeWizardPlansResponse(response.data)
    }),
    patchPrimaryInsurance: build.mutation<
      PatchPrimaryInsuranceResProps,
      PatchPrimaryInsuranceReqProps
    >({
      invalidatesTags: ['Insurance'],
      query: (body) => ({
        body,
        method: 'PATCH',
        url: '/patient-portal/insurance/primary'
      })
    }),
    /** Initiate a wizard eligibility check; returns immediately with a pending checkId. */
    postEligibilityCheck: build.mutation<
      PostEligibilityCheckResProps,
      PostEligibilityCheckReqProps
    >({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/patient-portal/insurance/eligibility/checks'
      })
    }),
    postPrimaryInsuranceCommit: build.mutation<
      PrimaryInsuranceCommitResProps,
      PrimaryInsuranceCommitReqProps
    >({
      invalidatesTags: ['Insurance'],
      query: (body) => ({
        body,
        method: 'POST',
        url: '/patient-portal/insurance/primary/commit'
      })
    }),
    postPrimaryInsuranceDeletePreview: build.mutation<
      DeletePrimaryPreviewResProps,
      DeletePrimaryPreviewReqProps
    >({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/patient-portal/insurance/primary/delete/preview'
      })
    }),
    postPrimaryInsurancePreview: build.mutation<
      PrimaryInsurancePreviewResProps,
      PrimaryInsurancePreviewReqProps
    >({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/patient-portal/insurance/primary/preview'
      })
    }),
    postValidateMedicareNumber: build.mutation<ValidateMedicareResProps, ValidateMedicareReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/patient-portal/insurance/validate-medicare'
      })
    }),
    updateInsurance: build.mutation<DeleteInsuranceResProps, UpdateInsuranceReqProps>({
      invalidatesTags: ['Insurance'],
      query: ({ _id, ...body }) => ({
        body,
        method: 'PATCH',
        url: `/insurances/${_id}`
      })
    })
  }),
  reducerPath: 'insuranceApi',
  tagTypes: ['Insurance']
});

export const {
  useAddInsuranceMutation,
  useDeleteInsuranceMutation,
  useDeletePrimaryInsuranceMutation,
  useDeleteSecondaryInsuranceMutation,
  useGetBenefitInsuranceStatusQuery,
  useGetInsuranceProvidersQuery,
  useGetInsurancesQuery,
  useGetMedicareConsentRequiredQuery,
  useGetWizardInsurancePlansQuery,
  useLazyGetEligibilityCheckQuery,
  useLazyGetInsuranceProvidersQuery,
  useLazyGetInsurancesQuery,
  usePatchPrimaryInsuranceMutation,
  usePostEligibilityCheckMutation,
  usePostPrimaryInsuranceCommitMutation,
  usePostPrimaryInsuranceDeletePreviewMutation,
  usePostPrimaryInsurancePreviewMutation,
  usePostValidateMedicareNumberMutation,
  useUpdateInsuranceMutation
} = insuranceApi;
